// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import requestWishlistQuote from "@bg/App/Handlers/Data/WishLists/RequestQuoteFromWishlist";
import Button, { ButtonPresentationProps } from "@bg/MobiusOverrides/Button/Button";
import LazyImage, { LazyImageProps } from "@bg/MobiusOverrides/LazyImage";
import { fontConfig } from "@bg/XDL/PreStyle/typography";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import addWishListToCart from "@insite/client-framework/Store/Pages/Cart/Handlers/AddWishListToCart";
import translate from "@insite/client-framework/Translate";
import { CartLineCollectionModel, WishListModel } from "@insite/client-framework/Types/ApiModels";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import MyListsDetailsPageTypeLink from "@insite/content-library/Components/MyListsDetailsPageTypeLink";
import { ButtonIcon } from "@insite/mobius/Button";
import DataTableCell from "@insite/mobius/DataTable/DataTableCell";
import DataTableRow, { DataTableRowProps } from "@insite/mobius/DataTable/DataTableRow";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import Typography from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { useContext } from "react";
import { connect, HandleThunkActionCreator } from "react-redux";
import { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => ({
    addingProductToCart: state.context.addingProductToCart,
});

export interface RecentWishListStyles {
    tablerow?: DataTableRowProps;
    imagesAndCount?: InjectableCss;
    productImagesGridItem?: InjectableCss;
    productImageLink?: LinkPresentationProps;
    productImage?: LazyImageProps;
    linesCount?: InjectableCss;
    actionsWrapper?: InjectableCss;
    requestQuoteLink?: LinkPresentationProps;
    addToCart?: ButtonPresentationProps;
}

export const styles: RecentWishListStyles = {
    tablerow: {
        css: css`
            td:not(.actions),
            td:not(.actions) span {
                font-weight: ${fontConfig.weight.semiBold};
                color: ${getColor("secondary")};
            }
        `,
    },
    imagesAndCount: {
        css: css`
            display: flex;
            align-items: center;
            width: 100%;
            justify-content: center;
        `,
    },
    productImageLink: {
        css: css`
            &&:not(:last-child) {
                margin-right: 20px;
            }
        `,
    },
    productImagesGridItem: {
        css: css`
            width: 250px;
            margin-right: 45px;

            @media (max-width: 1024px) {
                width: 100%;
                min-width: 250px;
                margin-bottom: 10px;
            }
            @media (max-width: 600px) {
                margin-right: 0;
            }
        `,
    },
    productImage: {
        css: css`
            max-width: 70px;
            img {
                height: 70px;
            }
        `,
        errorTypographyProps: {
            size: 11,
            css: css`
                max-height: 65px;
            `,
        },
    },
    actionsWrapper: {
        css: css`
            width: 100%;
            display: flex;
            align-items: center;
            justify-content: flex-end;
        `,
    },
    addToCart: {
        css: css`
            &&&&&& {
                background: ${getColor("primary.main")};
                padding: 0 20px;
                max-width: 75px;

                &:hover,
                &:focus {
                    background: ${getColor("common.white")};
                    border-color: ${getColor("primary.main")};
                    &&& svg {
                        color: ${getColor("primary.main")};
                    }
                }
                &.loading-bgs {
                    border: 0 !important;
                }
            }
        `,
    },
};

interface OwnProps {
    wishList: WishListModel;
    requestQuoteHandler: HandleThunkActionCreator<typeof requestWishlistQuote>;
    addWishListToCart: HandleThunkActionCreator<typeof addWishListToCart>;
}

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

const RecentWishListCard: React.FC<Props> = ({
    wishList,
    requestQuoteHandler,
    addWishListToCart,
    addingProductToCart,
}) => {
    if (!wishList) {
        return null;
    }
    const toasterContext = useContext(ToasterContext);
    const [isLoading, setIsLoading] = React.useState(false);

    const clickAddToCartHandler = (e: any) => {
        e.preventDefault();
        setIsLoading(true);
        addWishListToCart({
            apiParameter: { wishListId: wishList.id },
            onSuccess: onAddAllToCartSuccess,
            onComplete(resultProps) {
                if (resultProps.apiResult) {
                    // "this" is targeting the object being created, not the parent SFC
                    // eslint-disable-next-line react/no-this-in-sfc
                    this.onSuccess?.(resultProps.apiResult);
                }
            },
        });
    };

    const requestQuoteForWishlist = () => {
        requestQuoteHandler({
            apiParameter: {
                wishlistId: wishList.id,
            },
        });
    };

    const onAddAllToCartSuccess = (result: CartLineCollectionModel) => {
        setIsLoading(false);
        toasterContext.addToast({
            body: result.notAllAddedToCart
                ? siteMessage("Lists_Items_Not_All_Added_To_Cart")
                : translate("List Added to Cart"),
            messageType: "success",
        });
    };

    return (
        <DataTableRow
            data-test-selector="myProjectList_wishListGrid"
            key={`myProjectList_${wishList.id}`}
            {...styles.tablerow}
        >
            <DataTableCell
                data-test-selector="myProjectList_wishListName"
                css={css`
                    max-width: 150px;
                    a {
                        max-width: 100%;
                    }
                    a span {
                        text-overflow: ellipsis;
                        height: 18px;
                        overflow: hidden;
                    }
                `}
            >
                <MyListsDetailsPageTypeLink
                    title={wishList.name}
                    wishListId={wishList.id}
                    testSelector="wishListCardName"
                />
            </DataTableCell>
            <DataTableCell data-test-selector="myProjectList_wishListLastUpdated">
                <LocalizedDateTime dateTime={wishList.updatedOn} />
            </DataTableCell>
            <DataTableCell data-test-selector="myProjectList_wishListTotal">
                <StyledWrapper {...styles.imagesAndCount}>
                    {wishList.wishListLineCollection && wishList.wishListLineCollection.length > 0 && (
                        <StyledWrapper {...styles.productImagesGridItem}>
                            {wishList.wishListLineCollection.slice(0, 3).map(line => (
                                <Link key={line.id.toString()} {...styles.productImageLink} href={line.productUri}>
                                    <LazyImage
                                        {...styles.productImage}
                                        cloudinaryWidth="70"
                                        cloudinary={true}
                                        src={line.smallImagePath}
                                        altText={line.altText}
                                    />
                                </Link>
                            ))}
                        </StyledWrapper>
                    )}
                    {wishList.wishListLinesCount > 0 && (
                        <StyledWrapper {...styles.linesCount}>
                            <Typography lineHeight="20px">
                                {wishList.wishListLinesCount}&nbsp;{translate("Items")}
                            </Typography>
                        </StyledWrapper>
                    )}
                </StyledWrapper>
            </DataTableCell>
            <DataTableCell className="actions">
                <StyledWrapper {...styles.actionsWrapper}>
                    {wishList.canAddAllToCart && (
                        <Link
                            onClick={requestQuoteForWishlist}
                            color="primary"
                            css={css`
                                margin-right: 20px;
                            `}
                            disabled={addingProductToCart || !wishList.wishListLineCollection?.length}
                        >
                            {translate("Request Quote")}
                        </Link>
                    )}
                    {wishList.canAddAllToCart && (
                        <Button
                            {...styles.addToCart}
                            className={`${addingProductToCart && isLoading ? "loading-bgs" : ""}`}
                            disabled={addingProductToCart || !wishList.wishListLineCollection?.length}
                            onClick={clickAddToCartHandler}
                        >
                            {addingProductToCart && isLoading ? (
                                <LoadingSpinner size={20} color="common.white" />
                            ) : (
                                <ButtonIcon src="Cart" color="common.white" />
                            )}
                        </Button>
                    )}
                </StyledWrapper>
            </DataTableCell>
        </DataTableRow>
    );
};

export default connect(mapStateToProps)(RecentWishListCard);
